/**
 * 设备类型定义和协议类型
 * 包含设备分类、连接类型等基础类型定义
 */

export type DeviceCategory = 'headPhone' | 'microPhone' | 'unknown'
export type ConnectionType = 'USB' | '2.4G' | 'bluetooth' | null
export type DeviceColor = 'black' | 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'indigo' | 'purple' | 'white' | 'unknown'

/**
 * 设备能力配置接口
 * 用于标记设备支持的功能，便于 UI 自适应和功能开关
 * 支持未来扩展新设备类型
 */
export interface DeviceCapabilities {
  // 音频功能
  hasEq: boolean              // EQ均衡器
  hasNoiseReduction: boolean  // 降噪功能（AI ENC / ENC）
  hasVoiceChange: boolean     // 魔幻音效/变声
  hasSpecialAudio: boolean    // 特殊音频（一键吃鸡等）

  // 连接功能
  hasBluetooth: boolean      // 蓝牙功能
  has24G: boolean             // 2.4G无线
  hasUsb: boolean             // USB连接

  // 升级功能
  hasFirmwareUpgrade: boolean // 固件升级
  hasDongleUpgrade: boolean  // Dongle升级
  hasBluetoothUpgrade: boolean // 蓝牙升级

  // 其他功能
  hasLed: boolean             // LED/RGB灯效
  hasHotkey: boolean          // 热键设置
  hasLanguageSwitch: boolean  // 语言切换
  hasPowerControl: boolean   // 电源控制（开关机）
}

// 默认设备能力配置
export const DEFAULT_DEVICE_CAPABILITIES: DeviceCapabilities = {
  hasEq: false,
  hasNoiseReduction: false,
  hasVoiceChange: false,
  hasSpecialAudio: false,
  hasBluetooth: false,
  has24G: false,
  hasUsb: false,
  hasFirmwareUpgrade: false,
  hasDongleUpgrade: false,
  hasBluetoothUpgrade: false,
  hasLed: false,
  hasHotkey: false,
  hasLanguageSwitch: false,
  hasPowerControl: false,
}

// 后续扩展新设备
// 只需在 DEVICE_CAPABILITY_MAP 添加新设备类型配置即可：
// 设备类型能力配置映射
export const DEVICE_CAPABILITY_MAP: Record<DeviceCategory, Partial<DeviceCapabilities>> = {
  headPhone: {
    hasEq: true,
    hasNoiseReduction: true,      // AI ENC
    hasVoiceChange: false,        // 魔幻音效已移除
    hasSpecialAudio: true,
    hasBluetooth: true,
    has24G: true,
    hasUsb: true,
    hasFirmwareUpgrade: true,
    hasDongleUpgrade: true,
    hasBluetoothUpgrade: true,
    hasLed: true,
    hasHotkey: true,
    hasLanguageSwitch: false,     // 耳机隐藏语言切换
    hasPowerControl: true,        // 仅2.4G模式下可用
  },
  microPhone: {
    hasEq: true,
    hasNoiseReduction: true,      // ENC
    hasVoiceChange: false,
    hasSpecialAudio: false,
    hasBluetooth: false,
    has24G: false,
    hasUsb: true,
    hasFirmwareUpgrade: false,    // 麦克风无固件升级
    hasDongleUpgrade: false,
    hasBluetoothUpgrade: false,
    // DM05 USB MIC 支持 RGB，旧项目会显示麦克风专用灯效列表。
    hasLed: true,
    hasHotkey: false,             // 麦克风无热键
    hasLanguageSwitch: true,      // 麦克风显示语言切换
    hasPowerControl: false,
  },
  unknown: {},
}

export interface DeviceInfo {
  productId: number
  vendorId: number
  productName: string
  serialNumber?: string
  opened: boolean
}

export interface DeviceStatus {
  callBridgeBtEnabled?: number       // B[5] bit1
  callBridgeDongleEnabled?: number   // B[5] bit2
  callBridgeUsbEnabled?: number      // B[5] bit3
  connectionType?: ConnectionType
  isDongleConnection?: boolean
  micByte?: number
  speakerByte?: number
  chickenByte?: number
  usbChickenByte?: number
  dongleChickenByte?: number
  eqByte?: number
  eqTagByte?: number
  usbEqControlByte?: number
  dongleEqControlByte?: number
  voiceByte?: number             // B[7] 原始字节
  voiceChangeEnabled?: number    // B[7] bit7 变声开关
  voiceChangeMode?: number       // B[7] bit0..6，当前定义 0-3
  sidetoneByte?: number          // B[8] 原始字节
  bluetoothStatusByte?: number   // B[18] 原始字节；连接兼容字段仅取 bit0
  audioStreamByte?: number       // B[44] 原始字节
  hardwareMuteByte?: number      // B[45] 原始字节
  encryptData?: number[]         // B[20]..B[27]，不在前端解释加密内容
  scenarioValue?: number         // B[46] 场景原始值，暂不据此改写场景 UI
  reserved47to53?: number[]      // B[47]..B[53] 预留
  reserved46to53?: number[]      // 保留旧字段名，仍包含 B[46]
  rawPayloadBytes?: number[]     // 原始 B[0]..B[53]，不受后续 UI 归一化影响
  headsetConnected: number
  micVolumeLevel: number
  micMute: number
  usbMicByte?: number
  usbMicMute?: number
  usbMicVolumeLevel?: number
  dongleMicByte?: number
  dongleMicMute?: number
  dongleMicVolumeLevel?: number
  speakerVolumeLevel: number
  speakerMute: number
  usbSpeakerByte?: number
  usbSpeakerMute?: number
  usbSpeakerVolumeLevel?: number
  dongleSpeakerByte?: number
  dongleSpeakerMute?: number
  dongleSpeakerVolumeLevel?: number
  splitVolume?: number          // B[3] Split 比例 0-200
  chickenOneKey: number         // 当前链路一键吃鸡开关 0/1；原始字节用 chickenByte
  chickenTag?: number
  usbChickenOneKey?: number
  usbChickenTag?: number
  dongleChickenOneKey?: number
  dongleChickenTag?: number
  noiseReductionMode: number
  noiseReductionEnabled: number
  noiseByte?: number
  ledMode: number
  eqMode: number
  eqEnabled?: number
  eqTag?: number
  eqControlByte?: number
  usbEqEnabled?: number
  usbEqMode?: number
  usbEqTag?: number
  dongleEqEnabled?: number
  dongleEqMode?: number
  dongleEqTag?: number
  linkStatus: number
  batteryLevel: number
  powerOffStatus: number
  headsetColor: number
  batteryCharging: number       // B[17]：0 未充电，1 充电中，2 已充满
  bluetoothConnectionMark: number // B[18] bit0 兼容字段，只返回 0/1
  productType: number
  headsetColorName?: DeviceColor
  dongleVersion: string
  softwareVersion: string
  bluetoothVersion: string
  noiseCancelVersion: string
  audioChannelMode: number
  mixVolume: number             // B[2] Mix 比例 0-200
  adaptiveVolume?: number        // B[37] Adaptive 比例 0-200
  callBridgeEnabled?: number     // 通话桥接 bit0: 0=关闭, 1=开启
  callBridgeByte?: number        // 0x80 B[5]：总开关和三条通路的完整低四位
  callBridgeChannelMask?: number // bit1=BT、bit2=2.4G、bit3=USB；总关闭时仍保留
  btShareEnabled?: number        // 蓝牙分享: 0=关闭, 1=开启
  // 耳返相关
  sidetoneEnabled?: number       // 耳返开关: 0=关闭, 1=开启
  sidetoneVolume?: number        // 耳返音量: 0-100
  // 蓝牙详细状态（新增）
  bluetoothConnected?: number    // 蓝牙连接状态: 0=未连接, 1=已连接
  bluetoothInCall?: number       // 蓝牙通话中: 0=否, 1=是
  bluetoothPlaying?: number      // 蓝牙播放中: 0=否, 1=是
  // 音频流状态（新增）
  dongleAudioPlaying?: number    // Dongle音频播放: 0=否, 1=是
  headsetAudioPlaying?: number   // 耳机音频播放: 0=否, 1=是
  // 硬件静音状态（新增）
  hardwareMuted?: number         // 硬件静音: 0=否, 1=是
}

export interface DeviceDetail {
  deviceCategory: DeviceCategory
  vendorId: number
  productId: number
  deviceName: string
  deviceSerial: string
  firmwareVersion: string
  hardwareVersion: string
  protocolVersion: string
  deviceTypeCode: string
  softwareVersion?: string
  bluetoothVersion?: string
  noiseCancelVersion?: string
  dongleVersion?: string
  /** 0x82 B[13] color value (Dongle when the detail frame belongs to the dongle). */
  color?: number
  colorName?: DeviceColor
}

// HID 协议命令类型
export enum CommandType {
  GET_STATUS = 0x80,
  GET_DEVICE_DETAIL = 0x82,
  SET_EQ = 0x10,
  SET_MUTE = 0x20,
  SET_MIC_MUTE = 0x21,
  SET_LED = 0x30,
  SET_VOICE_CHANGE = 0x40,
  SET_NOISE_REDUCTION = 0x50,
  // ... 其他命令
}

export interface ProtocolFrame {
  header: [number, number] // AA 53
  length: number
  command: CommandType
  data: number[]
  checksum: number
}

// 与 commands.ts 对齐的 EQ 频点类型
export type { EqDataPoint } from './commands'

// EQ 类型（UI 层简化结构，可选）
export interface EqPoint {
  frequency: string
  gain: number // -12 到 12 dB
}

export interface EqPreset {
  id: string
  name: string
  nameEn: string
  datapoint: EqPoint[]
  isCustom: boolean
}

export interface EqSettings {
  enabled: boolean
  currentPreset: string
  presets: EqPreset[]
  customPresets: EqPreset[]
}

// LED/RGB 类型
export interface RgbSettings {
  enabled: boolean
  mode: 'breathing' | 'static' | 'rainbow' | 'off'
  color: string // 十六进制颜色
  brightness: number // 0-100
}

// 变声类型
export type VoiceType = 'normal' | 'boy' | 'girl' | 'child' | 'robot' | 'monster'

export interface VoiceChangeSettings {
  enabled: boolean
  type: VoiceType
}

// 降噪类型
export interface NoiseReductionSettings {
  enabled: boolean
  level: number // 1-5
}

// 事件类型
export interface DeviceEventMap {
  'device-connected': DeviceDetail
  'device-disconnected': void
  'battery-changed': number
  'status-updated': DeviceStatus
  'data-received': Uint8Array
}

export type DeviceEventCallback<T extends keyof DeviceEventMap> = (
  data: DeviceEventMap[T]
) => void
